import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import usuarioService, { Usuario } from '../../services/usuarioService';
import { useAuth } from '../../contexts/AuthContext';
import CajasUsuarioTab from '../../components/sistema/CajasUsuarioTab';
import {
  canManageSistemaUsers,
  effectiveUserRoles,
  effectiveUsername,
} from '../../utils/sistemaAuthz';

interface UsuarioFormData {
  usuario: string;
  nombre: string;
  password: string;
  email: string;
  role: string;
}

const defaultForm: UsuarioFormData = {
  usuario: '',
  nombre: '',
  password: '',
  email: '',
  role: 'User',
};

/** Misma lógica de referencia que en API (EmailAddressAttribute es algo más permisiva; esto cubre casos habituales). */
const LOGIN_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const baseRoles = ['Admin', 'User', 'Vendedor', 'Supervisor', 'Operador'];

const UsuarioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { usuario: usuarioParam } = useParams();
  const [formData, setFormData] = useState<UsuarioFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [frozenRole, setFrozenRole] = useState<string>('User');

  const canManageUsers = useMemo(
    () => canManageSistemaUsers(effectiveUserRoles(user?.roles)),
    [user?.roles]
  );
  const selfLogin = useMemo(() => effectiveUsername(user?.username), [user?.username]);
  const isSelfProfileRoute = useMemo(
    () =>
      !!(
        selfLogin &&
        usuarioParam &&
        usuarioParam !== 'nuevo' &&
        selfLogin.toLowerCase() === usuarioParam.toLowerCase()
      ),
    [selfLogin, usuarioParam]
  );
  const isLimitedSelfProfile = !canManageUsers && isSelfProfileRoute;

  // Solo mostrar SUPERADMIN si el usuario actual es SUPERADMIN
  const isSuperAdmin = user?.roles?.includes('SUPERADMIN');
  const availableRoles = isSuperAdmin ? [...baseRoles, 'SUPERADMIN'] : baseRoles;

  const afterSaveOrCancelPath = canManageUsers ? '/sistema/usuarios' : '/menu-principal';

  const loadUsuario = useCallback(async () => {
    if (!usuarioParam || usuarioParam === 'nuevo') return;
    const manage = canManageSistemaUsers(effectiveUserRoles(user?.roles));
    const self = effectiveUsername(user?.username);
    if (!manage) {
      if (!self || usuarioParam.toLowerCase() !== self.toLowerCase()) return;
    }
    try {
      setLoading(true);
      const usuario = await usuarioService.getUsuario(usuarioParam);
      const role = usuario.role || 'User';
      setFrozenRole(role);
      setFormData({
        usuario: (usuario as any).usuario || (usuario as any).Usuario || (usuario as any).id || (usuario as any).Id || '',
        nombre: (usuario as any).username || (usuario as any).Username || (usuario as any).nombre || (usuario as any).Nombre || '',
        password: '',
        email: usuario.email || '',
        role,
      });
      setIsEdit(true);
    } catch {
      enqueueSnackbar('Error al cargar el usuario', { variant: 'error' });
      navigate(canManageSistemaUsers(effectiveUserRoles(user?.roles)) ? '/sistema/usuarios' : '/menu-principal');
    } finally {
      setLoading(false);
    }
  }, [usuarioParam, enqueueSnackbar, navigate, user?.roles, user?.username]);

  useEffect(() => {
    if (usuarioParam && usuarioParam !== 'nuevo') {
      loadUsuario();
    }
  }, [usuarioParam, loadUsuario]);

  useEffect(() => {
    if (isLimitedSelfProfile) setCurrentTab(0);
  }, [isLimitedSelfProfile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);

      if (!formData.usuario.trim()) {
        enqueueSnackbar('El ID de usuario es requerido', { variant: 'warning' });
        return;
      }

      if (!formData.nombre.trim()) {
        enqueueSnackbar('El nombre es requerido', { variant: 'warning' });
        return;
      }

      const usuarioTrim = formData.usuario.trim();
      if (usuarioTrim.length > 30) {
        enqueueSnackbar('El ID de usuario no puede superar los 30 caracteres', { variant: 'warning' });
        return;
      }

      const emailTrim = formData.email.trim();
      if (emailTrim && !LOGIN_EMAIL_REGEX.test(emailTrim)) {
        enqueueSnackbar('El email opcional no tiene un formato válido', { variant: 'warning' });
        return;
      }

      if (!isLimitedSelfProfile) {
        if (formData.role.toUpperCase() === 'SUPERADMIN' && !isSuperAdmin) {
          enqueueSnackbar('Solo usuarios con rol SUPERADMIN pueden asignar este rol', { variant: 'error' });
          return;
        }
      }

      if (!isEdit && (!formData.password || formData.password.length < 6)) {
        enqueueSnackbar('La contraseña debe tener al menos 6 caracteres', { variant: 'warning' });
        return;
      }

      const roleForApi = isLimitedSelfProfile ? frozenRole : formData.role;

      const payload: Usuario = {
        id: usuarioTrim,
        usuario: usuarioTrim,
        username: formData.nombre.trim(),
        nombre: formData.nombre.trim(),
        ...(emailTrim ? { email: emailTrim } : {}),
        role: roleForApi,
        ...(formData.password && { password: formData.password }),
      };

      if (isEdit) {
        await usuarioService.updateUsuario(usuarioParam!, payload);
      } else {
        await usuarioService.createUsuario(payload);
      }

      enqueueSnackbar('Usuario guardado exitosamente', { variant: 'success' });
      navigate(afterSaveOrCancelPath);
    } catch (error: any) {
      console.error('Error al guardar usuario:', error);
      const errorMessage = error?.response?.data?.message || 'Error al guardar el usuario';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!canManageUsers && usuarioParam === 'nuevo') {
    return <Navigate to="/menu-principal" replace />;
  }

  const blockingOtherProfile =
    !canManageUsers &&
    !!usuarioParam &&
    usuarioParam !== 'nuevo' &&
    (!selfLogin || usuarioParam.toLowerCase() !== selfLogin.toLowerCase());

  if (blockingOtherProfile) {
    return (
      <Navigate
        to={selfLogin ? `/sistema/usuarios/${encodeURIComponent(selfLogin)}` : '/menu-principal'}
        replace
      />
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const pageTitle = isLimitedSelfProfile ? 'Mis datos' : isEdit ? 'Editar Usuario' : 'Nuevo Usuario';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {pageTitle}
          </Typography>

          {!isLimitedSelfProfile && (
            <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
              <Tab label="General" />
              <Tab label="Grupos de Caja" disabled={!isEdit} />
            </Tabs>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="usuario"
                    label={isEdit ? 'Usuario' : 'ID de usuario'}
                    type="text"
                    value={formData.usuario}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={isEdit || isLimitedSelfProfile}
                    inputProps={
                      isEdit || isLimitedSelfProfile
                        ? { autoComplete: 'off' }
                        : { maxLength: 30, autoComplete: 'username' }
                    }
                    helperText={
                      isLimitedSelfProfile || isEdit
                        ? 'ID de login'
                        : 'Identificador único para iniciar sesión (máx. 30 caracteres)'
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="nombre"
                    label="Nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    fullWidth
                    required
                    helperText="Nombre completo del usuario"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="email"
                    label="Email (opcional)"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    helperText="Mail de contacto y recuperación de contraseña"
                    inputProps={{ autoComplete: 'email' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {isLimitedSelfProfile ? (
                    <TextField
                      label="Rol"
                      value={formData.role}
                      fullWidth
                      disabled
                      helperText="Solo un administrador puede cambiar el rol"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ) : (
                    <FormControl fullWidth required>
                      <InputLabel>Rol</InputLabel>
                      <Select
                        name="role"
                        value={formData.role}
                        onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                        label="Rol"
                        sx={{ borderRadius: 2 }}
                      >
                        {availableRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="password"
                    label={isEdit ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    required={!isEdit}
                    helperText={isEdit ? 'Dejar vacío si no desea cambiar la contraseña' : 'Mínimo 6 caracteres'}
                    inputProps={{ autoComplete: 'new-password' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {!isLimitedSelfProfile && currentTab === 1 && (
              <CajasUsuarioTab usuario={formData.usuario} username={formData.nombre} disabled={!isEdit} />
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(afterSaveOrCancelPath)}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UsuarioForm;
